// components/ProcessingPopup.jsx
import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, AlertCircle, X, Users } from 'lucide-react';

const ProcessingPopup = ({ 
  isOpen, 
  onClose, 
  brandName, 
  brandUrl, 
  processingStatus = '1',
  isCompleted = false,
  hasError = false,
  liveAffiliatesCount = 0
}) => {
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [showPolling, setShowPolling] = useState(false);

  // Processing steps with their descriptions
  const processingSteps = [
    { step: '1', label: 'Brand Setup', description: 'Initializing brand analysis...' },
    { step: '2', label: 'Backlink Fetch', description: 'Collecting backlink data...' },
    { step: '3', label: 'Google Search', description: 'Analyzing search patterns...' },
    { step: '4', label: 'Classification', description: 'Categorizing content...' },
    { step: '5', label: 'Backlink Processing', description: 'Processing backlink insights...' },
    { step: '6', label: 'Organic Keywords', description: 'Analyzing organic keywords...' },
    { step: '7', label: 'Paid Keywords', description: 'Processing paid keywords...' },
    { step: '8', label: 'Geographic Data', description: 'Finalizing geographic analysis...' }
  ];

  // Calculate progress percentage
  const getCurrentStepIndex = () => {
    if (isCompleted || processingStatus === 'completed') return 8;
    const currentStep = parseInt(processingStatus) || 1;
    return Math.min(currentStep, 8);
  };

  const progressPercentage = (getCurrentStepIndex() / 8) * 100;

  // Store onClose in a ref so the timer doesn't reset when onClose changes
  const onCloseRef = React.useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // 30-second countdown timer - auto close after 30 seconds
  useEffect(() => {
    let countdownTimer;
    let autoCloseTimer;

    if (isOpen) {
      // Countdown timer
      countdownTimer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Force close popup after 30 seconds
      autoCloseTimer = setTimeout(() => {
        onCloseRef.current();
      }, 30000);
    }

    return () => {
      if (countdownTimer) clearInterval(countdownTimer);
      if (autoCloseTimer) clearTimeout(autoCloseTimer);
    };
  }, [isOpen]);

  // Reset states when popup opens
  useEffect(() => {
    if (isOpen) {
      setTimeRemaining(30);
      setShowPolling(false);
    }
  }, [isOpen]);

  // Auto-close when completed
  useEffect(() => {
    if (isCompleted && showPolling) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000); // Show completed state for 3 seconds

      return () => clearTimeout(timer);
    }
  }, [isCompleted, showPolling, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-6 h-6" />
              <div>
                <h3 className="font-semibold text-lg">Competitor Analysis</h3>
                <p className="text-blue-100 dark:text-blue-200 text-sm">{brandName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-blue-100 dark:text-blue-200 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {!showPolling ? (
            // Initial 30-second loading
            <div className="text-center">
              <div className="mb-6">
                <Loader2 className="w-12 h-12 animate-spin text-blue-600 dark:text-blue-500 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-gray-800 dark:text-slate-100 mb-2">
                  Starting Analysis...
                </h4>
                <p className="text-gray-600 dark:text-slate-400 text-sm">
                  Preparing to analyze {brandUrl}
                </p>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                  {timeRemaining}s
                </div>
                <div className="text-sm text-gray-600 dark:text-slate-400">
                  Initializing analysis pipeline...
                </div>
              </div>
            </div>
          ) : (
            // Polling with step-by-step progress
            <div>
              {isCompleted ? (
                // Completed state
                <div className="text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 dark:text-green-400 mx-auto mb-4" />
                  <h4 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-2">
                    Analysis Complete!
                  </h4>
                  <p className="text-gray-600 dark:text-slate-400 mb-6">
                    Successfully analyzed {brandName}
                  </p>

                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
                      <span className="text-sm font-medium text-green-800 dark:text-green-300">Live Affiliates</span>
                    </div>
                    <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {liveAffiliatesCount}
                    </div>
                  </div>
                </div>
              ) : hasError ? (
                // Error state
                <div className="text-center">
                  <AlertCircle className="w-16 h-16 text-red-500 dark:text-red-400 mx-auto mb-4" />
                  <h4 className="text-xl font-semibold text-gray-800 dark:text-slate-100 mb-2">
                    Analysis Failed
                  </h4>
                  <p className="text-gray-600 dark:text-slate-400 mb-4">
                    There was an error processing {brandName}
                  </p>
                  <button
                    onClick={onClose}
                    className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              ) : (
                // Processing state with steps
                <div>
                  <div className="text-center mb-6">
                    <Loader2 className="w-10 h-10 animate-spin text-blue-600 dark:text-blue-500 mx-auto mb-3" />
                    <h4 className="text-lg font-medium text-gray-800 dark:text-slate-100 mb-1">
                      Processing Live Affiliates
                    </h4>
                    <p className="text-gray-600 dark:text-slate-400 text-sm">
                      Step {getCurrentStepIndex()} of 8
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-6">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-slate-400 mb-2">
                      <span>Progress</span>
                      <span>{Math.round(progressPercentage)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-blue-600 dark:from-blue-600 dark:to-blue-700 h-2 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Processing Steps */}
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {processingSteps.map((step, index) => {
                      const stepNumber = parseInt(step.step);
                      const currentStep = getCurrentStepIndex();
                      const isCompleted = stepNumber < currentStep;
                      const isCurrent = stepNumber === currentStep;
                      const isPending = stepNumber > currentStep;

                      return (
                        <div
                          key={step.step}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                            isCompleted ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700' :
                            isCurrent ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700' :
                            'bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700'
                          }`}
                        >
                          <div className="flex-shrink-0">
                            {isCompleted ? (
                              <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400" />
                            ) : isCurrent ? (
                              <Loader2 className="w-5 h-5 text-blue-500 dark:text-blue-400 animate-spin" />
                            ) : (
                              <div className="w-5 h-5 rounded-full border-2 border-gray-300 dark:border-slate-600 flex items-center justify-center">
                                <span className="text-xs text-gray-400 dark:text-slate-500">{stepNumber}</span>
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className={`font-medium text-sm ${
                              isCompleted ? 'text-green-700 dark:text-green-300' :
                              isCurrent ? 'text-blue-700 dark:text-blue-300' :
                              'text-gray-500 dark:text-slate-400'
                            }`}>
                              {step.label}
                            </div>
                            <div className={`text-xs ${
                              isCompleted ? 'text-green-600 dark:text-green-400' :
                              isCurrent ? 'text-blue-600 dark:text-blue-400' :
                              'text-gray-400 dark:text-slate-500'
                            }`}>
                              {step.description}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProcessingPopup;